import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { ThemeColors } from "./Scene";
import { LANE_WIDTH } from "@shared/constants";
import type { Path } from "./Path";
import { addThemeDecor } from "./decor";
import { buildRunnerCharacter } from "./runnerCharacter";
import type { HatId, PatternId } from "@shared/types";

// Random crowd character — same body style as the main runner, just a
// random shirt / pants / hat / pattern combo so the finish-line crowd feels
// like a stadium full of fans cheering you on.
const CROWD_SHIRTS = [
  "#ff5fa7", "#ffd166", "#6bd0ff", "#7c3aed", "#34d399",
  "#fb7185", "#f97316", "#a855f7", "#22d3ee", "#facc15",
  "#06d6a0", "#ef476f", "#ffe066", "#8fe7e2", "#d29bff",
];
const CROWD_PANTS = ["#312e81", "#0f172a", "#4c1d95", "#1e3a8a", "#064e3b"];
const CROWD_SKIN = ["#f6c89f", "#e0a26c", "#c98554", "#a96334", "#7a4823"];
const CROWD_HATS: HatId[] = ["none", "cap-blue", "cap-red", "beanie", "crown", "party-hat", "headband", "bow"];
const CROWD_PATTERNS: PatternId[] = ["solid", "stripes", "dots", "star", "zigzag"];
function pick<T>(arr: readonly T[]): T { return arr[Math.floor(Math.random() * arr.length)]!; }
/** Full crowd character with animation handles (body + legs) so callers can
 *  make the stands cheer + jump. {@link makeCrowdFan} wraps this when only the
 *  group is needed. */
export function makeCheeringFan(seedIdx: number) {
  const built = buildRunnerCharacter({
    shirtColor: pick(CROWD_SHIRTS),
    pantsColor: pick(CROWD_PANTS),
    skinColor: pick(CROWD_SKIN),
    hatId: pick(CROWD_HATS),
    patternId: pick(CROWD_PATTERNS),
  });
  // Smaller scale so each fan fits a stadium-seat footprint.
  built.group.scale.setScalar(0.42);
  // Slight rotation variation so the crowd doesn't look like clones.
  built.group.rotation.y = (seedIdx * 0.37) % (Math.PI * 2);
  return built;
}

export function makeCrowdFan(seedIdx: number) {
  return makeCheeringFan(seedIdx).group;
}

const SEGMENT_LENGTH = 6; // shorter so curves bend smoothly
const PATH_WIDTH = LANE_WIDTH * 5 + 1.6;

/**
 * The world bends along a curved Path. Stone tiles, parapet walls, and
 * theme terrain all follow the curve. Spectators cheer at the finish, and
 * a per-theme decoration layer (snow, birds, bears) brings the surroundings
 * to life.
 */
export class World {
  group = new THREE.Group();
  /** All standable surfaces this World contributes. Game queries this
   *  to build the collision raycast set — without it the default-mode
   *  path tiles would have no collision and the player would fall
   *  straight through the floor since the y=0 ground catch is gone. */
  supportMeshes: THREE.Object3D[] = [];
  private courseLength: number;
  private spectators: { mesh: THREE.Object3D; phase: number; baseY: number }[] = [];
  private decorTick: ((now: number) => void) | null = null;
  private noWalls = false;
  private hideGround = false;
  private hideFinish = false;

  constructor(theme: ThemeColors, courseLength: number, path: Path, opts: { noWalls?: boolean; hideGround?: boolean; hideFinish?: boolean; ambientDecor?: boolean; skyClouds?: boolean } = {}) {
    this.courseLength = courseLength;
    this.noWalls = !!opts.noWalls;
    this.hideGround = !!opts.hideGround;
    this.hideFinish = !!opts.hideFinish;
    const ambientDecor = opts.ambientDecor !== false;
    void ambientDecor;

    // Default cloud floor removed — the whole game is now "sky
    // platforms": walk off the edge and you fall into pure sky until
    // the 2s respawn timer fires. Keeps every level visually consistent.
    void makeCloudFloorTexture;

    if (!this.hideGround) this.buildPathTiles(theme, path, courseLength);
    // Start guard removed — was blocking the lobby's view of the player line-up
    // before the countdown finished.
    void theme;
    void this.buildStartGuard;

    // Finish line + grandstands at end-of-path location. Survival modes
    // (double-spinner) skip this — there's no finish to march toward.
    if (!this.hideFinish) {
      const finishS = path.length;
      const finishPos = path.positionAt(finishS, 0);
      const finishYaw = path.yawAt(finishS);
      const finish = this.buildFinish(theme);
      finish.position.copy(finishPos);
      finish.rotation.y = finishYaw;
      this.group.add(finish);
      // The grandstand is ~69 full character models (hundreds of draw calls) +
      // per-frame cheer animation — by far the heaviest decor. Skip it on the
      // low tier (ambientDecor off) so old tablets keep their frame rate.
      if (ambientDecor) this.buildGrandstand(path);
    }

    // Theme decorations (snow particles, jungle birds, etc.) — skipped
    // on low quality to drop the per-frame decor tick + extra draw calls.
    if (ambientDecor) {
      this.decorTick = addThemeDecor(this.group, path, theme, courseLength, {
        skyClouds: opts.skyClouds !== false,
      });
    }
  }

  /** Per-frame: cheering spectators + theme decor (snow falls, birds flap). */
  update(t: number) {
    for (const s of this.spectators) {
      s.mesh.position.y = s.baseY + Math.abs(Math.sin(t * 0.006 + s.phase)) * 0.45;
    }
    this.decorTick?.(t);
  }

  private buildPathTiles(theme: ThemeColors, path: Path, courseLength: number) {
    const lightMat = new THREE.MeshStandardMaterial({ color: theme.pathLight });
    const darkMat = new THREE.MeshStandardMaterial({ color: theme.pathDark });
    const wallMat = new THREE.MeshStandardMaterial({ color: theme.pathDark });
    const wallGeo = new SoftBox(0.6, 1.0, SEGMENT_LENGTH * 1.05);

    // Hit-Parade course has a gap in the floor that the player jumps across
    // via the moving platform. World skips tiles whose segment overlaps the
    // gap range.
    const gap = path.hitParade ? { start: path.hitParade.gapStart, end: path.hitParade.gapEnd } : null;
    const inGap = (a: number, b: number) => gap != null && a < gap.end && b > gap.start;

    let alt = false;
    for (let s = 0; s < courseLength + SEGMENT_LENGTH; s += SEGMENT_LENGTH) {
      const center = Math.min(courseLength, s + SEGMENT_LENGTH / 2);
      const segStart = s;
      const segEnd = s + SEGMENT_LENGTH;
      if (inGap(segStart, segEnd)) {
        // Skip the floor entirely in this segment — the player needs the
        // platform to cross. Toggle the alt stripe so the pattern resumes.
        alt = !alt;
        continue;
      }
      const pos = path.positionAt(center, 0);
      const yaw = path.yawAt(center);

      const narrowHalfWidth = path.narrowBridgeHalfWidthAt(center);
      const pathWidth = narrowHalfWidth ? narrowHalfWidth * 2 : PATH_WIDTH;
      // Walkable surfaces are kept at ≥ 0.5m thick everywhere — the
      // thin 0.4m tiles read as paper from the side.
      const TILE_THICKNESS = 0.6;
      const tile = new THREE.Mesh(
        new SoftBox(pathWidth, TILE_THICKNESS, SEGMENT_LENGTH * 1.05),
        alt ? lightMat : darkMat,
      );
      tile.position.copy(pos);
      tile.position.y = pos.y - TILE_THICKNESS / 2;
      tile.rotation.y = yaw;
      tile.rotation.x = -Math.atan(path.heightSlopeAt(center));
      tile.receiveShadow = true;
      // Real collision — without this the player has no floor to stand
      // on in default / hit-parade modes (the implicit y=0 ground
      // catch was removed for the sandbox refactor).
      this.supportMeshes.push(tile);
      this.group.add(tile);

      // Side parapets — omitted on the narrow swinging-log bridge and on
      // exposed-ledge courses (Hit Parade) where falling off is the point.
      if (!narrowHalfWidth && !this.noWalls) {
        for (const sign of [-1, 1] as const) {
          const wall = new THREE.Mesh(wallGeo, wallMat);
          const off = path.positionAt(center, sign * (PATH_WIDTH / 2 + 0.3));
          wall.position.copy(off);
          wall.position.y = off.y + 0.3;
          wall.rotation.y = yaw;
          wall.rotation.x = -Math.atan(path.heightSlopeAt(center));
          wall.castShadow = true;
          wall.receiveShadow = true;
          this.group.add(wall);
        }
      }
      const hNow = path.heightAt(center);
      const hNext = path.heightAt(Math.min(courseLength, center + SEGMENT_LENGTH));
      if (hNext - hNow > 0.85) {
        const face = new THREE.Mesh(
          new SoftBox(pathWidth + 0.2, hNext - hNow, 0.45),
          wallMat,
        );
        const fpos = path.positionAt(center + SEGMENT_LENGTH / 2, 0);
        face.position.copy(fpos);
        face.position.y = hNow + (hNext - hNow) / 2 - 0.02;
        face.rotation.y = yaw;
        face.castShadow = true;
        face.receiveShadow = true;
        this.group.add(face);
      }
      alt = !alt;
    }
  }

  private buildStartGuard(theme: ThemeColors, path: Path) {
    const mat = new THREE.MeshStandardMaterial({ color: theme.pathDark, roughness: 0.62 });
    const start = path.positionAt(0, 0);
    const tan = path.tangentAt(0);
    const yaw = path.yawAt(0);
    const guard = new THREE.Group();
    guard.position.set(start.x - tan.x * 0.85, start.y, start.z - tan.z * 0.85);
    guard.rotation.y = yaw;

    const rail = new THREE.Mesh(new SoftBox(PATH_WIDTH + 1.6, 0.9, 0.55), mat);
    rail.position.y = 0.55;
    rail.castShadow = true;
    rail.receiveShadow = true;
    guard.add(rail);

    const capMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.55 });
    const cap = new THREE.Mesh(new SoftBox(PATH_WIDTH + 1.9, 0.16, 0.62), capMat);
    cap.position.y = 1.08;
    cap.castShadow = true;
    cap.receiveShadow = true;
    guard.add(cap);

    this.group.add(guard);
  }

  /**
   * Build the three grandstands at the finish line. All positions come from
   * the Path so they always sit perpendicular to the path tangent at the
   * finish — even when the level ends inside a corner.
   *
   *   side stands: ±11m perpendicular, 4m back from the finish line
   *   back stand: 6m past the finish, facing back at the player
   */
  private buildGrandstand(path: Path) {
    const seatMat = new THREE.MeshStandardMaterial({ color: 0x4a4a5a });
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x3aa05c, roughness: 0.7 });

    const finishS = path.length;
    const yaw = path.yawAt(finishS);
    const finishPos = path.positionAt(finishS, 0);
    const tangent = path.tangentAt(finishS);
    // Height of the deck at the finish line. On flat courses this is 0;
    // on Level 1 (hit-parade) the finish sits ~22m up the ramp, and the
    // stands need to ride at that height too or the crowd renders at
    // ground level far below the visible track.
    const finishY = path.heightAt(finishS);

    // Side stands — placed using path.positionAt so the perpendicular
    // direction always matches the in-game lane offset convention. Each
    // stand sits on a green ground pad so it doesn't look like it's
    // floating above the void next to the cloud course.
    let fanIdx = 0;
    for (const side of [-1, 1] as const) {
      const center = path.positionAt(Math.max(0, finishS - 4), side * 11);
      const stand = new THREE.Group();
      stand.position.set(center.x, finishY, center.z);
      stand.rotation.y = yaw;
      // Ground pad under this stand — wide enough to bridge from the main
      // track edge out past the back of the seating.
      const pad = new THREE.Mesh(
        new SoftBox(10, 0.4, 18),
        groundMat,
      );
      pad.position.set(side * 2, -0.2, 0);
      pad.receiveShadow = true;
      stand.add(pad);
      for (let tier = 0; tier < 3; tier++) {
        const tierMesh = new THREE.Mesh(
          new SoftBox(5, 1.2 + tier * 0.4, 14),
          seatMat,
        );
        tierMesh.position.set(side * (tier * 1.6), 0.6 + tier * 1.2, 0);
        tierMesh.castShadow = true;
        tierMesh.receiveShadow = true;
        stand.add(tierMesh);
        for (let i = 0; i < 7; i++) {
          const baseY = 1.2 + tier * 1.2 + 0.22;
          const fan = makeCrowdFan(fanIdx++);
          fan.position.set(side * (tier * 1.6), baseY, -6 + i * 2);
          stand.add(fan);
          this.spectators.push({ mesh: fan, phase: tier * 1.3 + i * 0.7, baseY });
        }
      }
      this.group.add(stand);
    }

    // Back stand — extends straight off the end of the path along the finish
    // tangent. Ground pad bridges from the finish line out to the back row
    // so the stand isn't floating.
    const back = new THREE.Group();
    back.position.set(
      finishPos.x + tangent.x * 6,
      finishY,
      finishPos.z + tangent.z * 6,
    );
    back.rotation.y = yaw + Math.PI; // facing back at the runner
    const backPad = new THREE.Mesh(
      new SoftBox(24, 0.4, 14),
      groundMat,
    );
    backPad.position.set(0, -0.2, 2);
    backPad.receiveShadow = true;
    back.add(backPad);
    for (let tier = 0; tier < 3; tier++) {
      const tierMesh = new THREE.Mesh(
        new SoftBox(20, 1.2 + tier * 0.4, 4),
        seatMat,
      );
      tierMesh.position.set(0, 0.6 + tier * 1.2, tier * 1.6);
      tierMesh.castShadow = true;
      tierMesh.receiveShadow = true;
      back.add(tierMesh);
      for (let i = 0; i < 9; i++) {
        const baseY = 1.2 + tier * 1.2 + 0.22;
        const fan = makeCrowdFan(fanIdx++);
        fan.position.set(-8 + i * 2, baseY, tier * 1.6);
        back.add(fan);
        this.spectators.push({ mesh: fan, phase: tier * 1.1 + i * 0.5 + 2.4, baseY });
      }
    }
    this.group.add(back);

    // Checkered finish-line strip across the track, replacing the plain
    // banner stripe with a proper black/white car-race chequer.
    const checker = buildCheckeredStrip(PATH_WIDTH + 0.4);
    const finishPosOnTrack = path.positionAt(finishS, 0);
    checker.position.set(finishPosOnTrack.x, finishY + 0.05, finishPosOnTrack.z);
    checker.rotation.y = yaw;
    this.group.add(checker);
  }

  private buildFinish(theme: ThemeColors): THREE.Group {
    const g = new THREE.Group();
    void theme;
    const postMat = new THREE.MeshStandardMaterial({ color: 0xeeeeee });
    const postGeo = new SoftBox(0.5, 6, 0.5);
    for (const x of [-PATH_WIDTH / 2, PATH_WIDTH / 2]) {
      const post = new THREE.Mesh(postGeo, postMat);
      post.position.set(x, 3, 0);
      post.castShadow = true;
      g.add(post);
    }
    const banner = new THREE.Mesh(
      new SoftBox(PATH_WIDTH + 1, 1.2, 0.3),
      new THREE.MeshStandardMaterial({ color: 0xffd166 }),
    );
    banner.position.set(0, 5.5, 0);
    g.add(banner);
    return g;
  }

  get length() {
    return this.courseLength;
  }
}

let cloudFloorTexture: THREE.CanvasTexture | null = null;
function makeCloudFloorTexture() {
  if (cloudFloorTexture) return cloudFloorTexture;
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
  grad.addColorStop(0, "#95d9ff");
  grad.addColorStop(1, "#dff7ff");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < 90; i++) {
    const x = (i * 137.7) % canvas.width;
    const y = (i * 71.3) % canvas.height;
    const r = 38 + (i % 9) * 14;
    const puff = ctx.createRadialGradient(x, y, 0, x, y, r);
    puff.addColorStop(0, "rgba(255,255,255,0.9)");
    puff.addColorStop(0.58, "rgba(255,255,255,0.52)");
    puff.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = puff;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  cloudFloorTexture = new THREE.CanvasTexture(canvas);
  cloudFloorTexture.colorSpace = THREE.SRGBColorSpace;
  cloudFloorTexture.wrapS = THREE.RepeatWrapping;
  cloudFloorTexture.wrapT = THREE.RepeatWrapping;
  cloudFloorTexture.repeat.set(4, 4);
  return cloudFloorTexture;
}

/** Cartoon black/white car-race finish strip. Drawn as a flat plane with a
 *  canvas-baked checker texture so it costs one draw call and tiles nicely. */
function buildCheckeredStrip(width: number): THREE.Mesh {
  const tex = checkerTexture();
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    roughness: 0.6,
    metalness: 0.0,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, 2.6), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return mesh;
}
let _checkerTex: THREE.CanvasTexture | null = null;
function checkerTexture(): THREE.CanvasTexture {
  if (_checkerTex) return _checkerTex;
  const SIZE = 128;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE * 6;
  canvas.height = SIZE * 2;
  const ctx = canvas.getContext("2d")!;
  for (let y = 0; y < 2; y++) {
    for (let x = 0; x < 6; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "#0a0a0a" : "#fafafa";
      ctx.fillRect(x * SIZE, y * SIZE, SIZE, SIZE);
    }
  }
  _checkerTex = new THREE.CanvasTexture(canvas);
  _checkerTex.colorSpace = THREE.SRGBColorSpace;
  return _checkerTex;
}
